import { useState, useRef, useEffect } from 'react'
import Calendar from '../calendar/Calendar'
import styled from 'styled-components'
import dateFormats from '../../data/dateFormats.json'
import { Form } from '../form/FormComponents'
import useOutsideClick from '../../hooks/useOutsideClick'
import moment from 'moment/moment'
import * as AiIcon from 'react-icons/ai'
import useProfile from '../../hooks/useProfile'
import { snapshot } from 'valtio'

const Wrapper = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`

const DatePart = styled.div`
    position: relative;
    flex: 1;
`

const CalendarIcon = styled.div`
    position: absolute;
    top: 2px;
    right: 10px;
    font-size: 20px;
    pointer-events: none;
`

const TimeControl = styled(Form.Control)`
    width: 120px;
    flex-shrink: 0;
    min-width: 0;
    &::-webkit-calendar-picker-indicator {
        filter: ${({ theme }) => theme.mode === 'dark' ? 'invert(1)' : 'none'};
    }
`

const DateTimeInput = ({ name, id, value, setValue, align, testId, min, max, readOnly }) => {
    const { pState } = useProfile()
    const profile = snapshot(pState)
    const locale = dateFormats[profile.currentOrgCountry]

    const [displayDate, setDisplayDate] = useState('')
    const [displayTime, setDisplayTime] = useState('00:00')
    const [calDate, setCalDate] = useState(new Date())
    const [showCalendar, setShowCalendar] = useState(false)

    const menuRef = useRef(null)
    useOutsideClick(menuRef, () => setShowCalendar(false), 'date')

    useEffect(() => {
        if (!value) return
        const m = moment(value)
        if (!m.isValid()) return
        setDisplayDate(m.format(locale))
        setDisplayTime(m.format('HH:mm'))
        setCalDate(m.toDate())
    }, [value, locale])

    const emit = (dateObj, timeStr) => {
        const [h = 0, m = 0] = (timeStr || '00:00').split(':').map(Number)
        const result = moment(dateObj).hour(h).minute(m).second(0).millisecond(0)
        setValue({ target: { name, value: result.toISOString() } })
    }

    const handleDateInput = (e) => {
        const str = e.target.value
        setDisplayDate(str)
        const m = moment(str, locale, true)
        if (m.isValid()) {
            const d = m.toDate()
            setCalDate(d)
            emit(d, displayTime)
        }
    }

    const handleTimeInput = (e) => {
        const str = e.target.value
        setDisplayTime(str)
        if (/^\d{2}:\d{2}$/.test(str)) {
            emit(calDate, str)
        }
    }

    const handleDateSelect = (d) => {
        setCalDate(d)
        setDisplayDate(moment(d).format(locale))
        setShowCalendar(false)
        emit(d, displayTime)
    }

    if (readOnly) return (
        <Wrapper>
            <DatePart>
                <Form.Control
                    type="text"
                    value={displayDate}
                    readOnly
                    className="readonly"
                    tabIndex={-1}
                />
                <CalendarIcon><AiIcon.AiOutlineCalendar /></CalendarIcon>
            </DatePart>
            <TimeControl
                type="time"
                value={displayTime}
                readOnly
                className="readonly"
                tabIndex={-1}
            />
        </Wrapper>
    )

    return (
        <Wrapper>
            <DatePart>
                <Form.Control
                    type="text"
                    name={name}
                    id={id || name}
                    value={displayDate}
                    onChange={handleDateInput}
                    onClick={() => setShowCalendar(v => !v)}
                    autoComplete="off"
                    data-testid={testId}
                />
                <CalendarIcon><AiIcon.AiOutlineCalendar /></CalendarIcon>
                <Calendar
                    isVisible={showCalendar}
                    ref={menuRef}
                    date={calDate}
                    setDate={setCalDate}
                    onSelect={handleDateSelect}
                    $align={align || 'right'}
                    min={min}
                    max={max}
                />
            </DatePart>
            <TimeControl
                type="time"
                value={displayTime}
                onChange={handleTimeInput}
                step="300"
            />
        </Wrapper>
    )
}

export default DateTimeInput
